import {cn} from '@/lib/utils/cn';
import type {EdiEnvelopeExplained} from './lib/explainedEnvelope';

interface EnvelopeNavigatorProps {
  envelope: EdiEnvelopeExplained;
  activeGroupIndex: number;
  activeTransactionIndex: number;
  onSelect: (groupIndex: number, transactionIndex: number) => void;
}

/** A row of tabs for paging between transactions when an interchange
 * contains more than one GS group and/or more than one ST transaction.
 * Renders nothing at all for the common case of a single transaction. */
export function EnvelopeNavigator({
  envelope,
  activeGroupIndex,
  activeTransactionIndex,
  onSelect,
}: EnvelopeNavigatorProps) {
  const totalTransactions = envelope.groups.reduce(
    (sum, group) => sum + group.transactions.length,
    0
  );
  const showGroupLabel = envelope.groups.length > 1;
  if (totalTransactions <= 1) return null;

  return (
    <div
      role="tablist"
      aria-label="Transactions in this interchange"
      className="flex flex-wrap items-center gap-1"
    >
      {envelope.groups.map((group, groupIndex) =>
        group.transactions.map((transaction, transactionIndex) => {
          const isActive =
            groupIndex === activeGroupIndex &&
            transactionIndex === activeTransactionIndex;
          const identifier =
            transaction.parsed810?.header.big?.elements[1] ??
            transaction.parsed840?.header.bqt?.elements[1] ??
            transaction.parsed850?.header.beg?.elements[2] ??
            transaction.parsed852?.header.bct?.elements[2] ??
            transaction.parsed855?.header.bak?.elements[2] ??
            transaction.parsed856?.header.bsn?.elements[1] ??
            transaction.parsed843?.header.bqr?.elements[1] ??
            transaction.parsed844?.header.baa?.elements[4];
          const detail =
            typeof identifier === 'string' ? ` · ${identifier}` : '';

          return (
            <button
              key={`${groupIndex}-${transactionIndex}`}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onSelect(groupIndex, transactionIndex)}
              className={cn(
                'rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
                isActive
                  ? 'border-primary/30 bg-primary/10 text-primary'
                  : 'border-transparent text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              {showGroupLabel && <>Group {groupIndex + 1} · </>}
              {transaction.transactionSetCode || '?'}
              {detail}
            </button>
          );
        })
      )}
    </div>
  );
}
